import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';
import { getWeekDayLabel } from '../utils/dateLocale';

function coachSourceLabel(source, t) {
    if (source === 'once') return t('scheduleSessionDetail:coachOnce');
    if (source === 'permanent') return t('scheduleSessionDetail:coachPermanent');
    if (source === 'group') return t('scheduleSessionDetail:coachGroup');
    return '';
}

export default function ScheduleSessionDetailModal({
    open,
    slotId,
    sessionDate,
    schoolId,
    token,
    onClose,
    onEdit,
    onAttendance,
    canManage = false,
    isCoach = false
}) {
    const { t, i18n } = useTranslation(['scheduleSessionDetail', 'scheduleManager', 'common', 'nav', 'coach']);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !slotId || !sessionDate) {
            setDetail(null);
            setError('');
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        const api = axios.create({
            baseURL: getApiBaseUrl(),
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        api.get('/schedule/session-detail', {
            params: { slotId, date: sessionDate, schoolId: schoolId || undefined }
        })
            .then((r) => { if (!cancelled) setDetail(r.data); })
            .catch((err) => {
                if (!cancelled) {
                    setDetail(null);
                    setError(err.response?.data?.message || t('scheduleSessionDetail:loadError'));
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, slotId, sessionDate, schoolId, token, t, i18n.language]);

    if (!open) return null;

    const dateLabel = detail?.sessionDate
        ? formatDate(`${detail.sessionDate}T12:00:00`, {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        })
        : sessionDate;

    const studentsSummary = detail ? [
        t('scheduleSessionDetail:studentsBooked', { count: detail.expectedStudents ?? 0 }),
        t('scheduleSessionDetail:studentsConfirmed', { count: detail.confirmedCount ?? 0 }),
        (detail.declinedCount ?? 0) > 0
            ? t('scheduleSessionDetail:studentsDeclined', { count: detail.declinedCount })
            : null,
        (detail.pendingCount ?? 0) > 0
            ? t('scheduleSessionDetail:studentsPending', { count: detail.pendingCount })
            : null
    ].filter(Boolean).join(' · ') : '';

    const dayName = detail?.dayOfWeek
        ? getWeekDayLabel(detail.dayOfWeek, 'long')
        : '';

    return (
        <div
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.48)',
                zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff', borderRadius: '16px', maxWidth: '480px', width: '100%',
                    maxHeight: '90vh', overflowY: 'auto', padding: '22px',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.18)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {loading && <p style={{ margin: 0, color: '#64748b' }}>{t('common:loading')}</p>}
                {error && !loading && (
                    <>
                        <h3 style={{ margin: '0 0 8px', fontSize: '17px' }}>{t('scheduleSessionDetail:sessionDefault')}</h3>
                        <p style={{ color: '#dc2626', fontSize: '14px' }}>{error}</p>
                        <button type="button" onClick={onClose} style={ghostBtn}>{t('common:close')}</button>
                    </>
                )}
                {detail && !loading && (
                    <>
                        <div style={{ marginBottom: '14px' }}>
                            <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 800 }}>
                                {detail.groupName || t('scheduleSessionDetail:sessionDefault')}
                            </h3>
                            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>{dateLabel}</p>
                        </div>

                        <DetailRow icon="🕐" label={t('scheduleSessionDetail:time')}>
                            {detail.startTime} – {detail.endTime}
                            {detail.durationMinutes
                                ? ` (${t('scheduleSessionDetail:durationMin', { minutes: detail.durationMinutes })})`
                                : ''}
                        </DetailRow>

                        <DetailRow icon="📍" label={t('scheduleSessionDetail:room')}>
                            {detail.roomName || t('scheduleSessionDetail:notSpecified')}
                            {detail.roomAddress ? (
                                <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                    {detail.roomAddress}
                                </span>
                            ) : null}
                            {detail.roomType === 'rented' && detail.roomHourlyRate != null ? (
                                <span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
                                    {t('scheduleSessionDetail:rentRate', { rate: detail.roomHourlyRate })}
                                </span>
                            ) : null}
                        </DetailRow>

                        {detail.schoolName ? (
                            <DetailRow icon="🏫" label={t('scheduleSessionDetail:school')}>{detail.schoolName}</DetailRow>
                        ) : null}

                        <DetailRow icon="👥" label={t('scheduleSessionDetail:students')}>
                            {studentsSummary}
                        </DetailRow>

                        <DetailRow icon="🎽" label={t('scheduleSessionDetail:coaches')}>
                            {detail.coaches?.length ? (
                                <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                                    {detail.coaches.map((c) => (
                                        <li key={c.staffId ?? c.name} style={{ marginBottom: '4px' }}>
                                            {c.name}
                                            {coachSourceLabel(c.source, t) ? (
                                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>
                                                    {' · '}{coachSourceLabel(c.source, t)}
                                                </span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                detail.groupCoachName || t('scheduleSessionDetail:notAssigned')
                            )}
                        </DetailRow>

                        {detail.groupScheduleNote ? (
                            <DetailRow icon="📝" label={t('scheduleSessionDetail:note')}>{detail.groupScheduleNote}</DetailRow>
                        ) : null}

                        {(detail.validFrom || detail.validTo) ? (
                            <DetailRow icon="📅" label={t('scheduleSessionDetail:slotPeriod')}>
                                {t('scheduleSessionDetail:slotFrom', { from: detail.validFrom })}
                                {detail.validTo
                                    ? t('scheduleSessionDetail:slotTo', { to: detail.validTo })
                                    : t('scheduleSessionDetail:indefinitely')}
                                {dayName ? ` · ${dayName}` : ''}
                            </DetailRow>
                        ) : null}

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '18px' }}>
                            {canManage && onEdit ? (
                                <button type="button" onClick={() => onEdit(detail)} style={primaryBtn}>
                                    {t('scheduleSessionDetail:editSlot')}
                                </button>
                            ) : null}
                            {isCoach && onAttendance ? (
                                <button type="button" onClick={() => onAttendance(detail)} style={primaryBtn}>
                                    {t('scheduleSessionDetail:attendance')}
                                </button>
                            ) : null}
                            <button type="button" onClick={onClose} style={ghostBtn}>{t('common:close')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function DetailRow({ icon, label, children }) {
    return (
        <div style={{ marginBottom: '12px', fontSize: '14px', lineHeight: 1.45 }}>
            <div style={{ fontWeight: 600, color: '#334155', marginBottom: '2px' }}>{icon} {label}</div>
            <div style={{ color: '#475569' }}>{children}</div>
        </div>
    );
}

const primaryBtn = {
    padding: '10px 16px', background: '#4f46e5', color: '#fff',
    border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '14px'
};
const ghostBtn = {
    padding: '10px 16px', background: '#f1f5f9', color: '#334155',
    border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '14px'
};
